import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Heart, Lock, KeyRound, Loader2 } from "lucide-react";
import { api, localDate } from "@/lib/api";
import BackgroundDecor from "@/components/BackgroundDecor";

export default function PasswordGate({ onUnlock, names }) {
  const [riddle, setRiddle] = useState(null);
  const [answer, setAnswer] = useState("");
  const [error, setError] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    api.get(`/gate/riddle?client_date=${localDate()}`).then(({ data }) => setRiddle(data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!answer.trim()) return;
    setChecking(true);
    setError(false);
    try {
      const { data } = await api.post(`/gate/verify?client_date=${localDate()}`, { answer });
      if (data.ok) {
        onUnlock();
      } else {
        setError(true);
        setAnswer("");
      }
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="paper-grain min-h-screen flex items-center justify-center p-6">
      <BackgroundDecor />
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6 }}
        className="card-paper rounded-3xl p-8 md:p-12 max-w-lg w-full text-center relative z-10"
        data-testid="password-gate"
      >
        <div className="w-14 h-14 rounded-full bg-[#D95F59] mx-auto flex items-center justify-center mb-5">
          <Lock className="w-6 h-6 text-white" />
        </div>
        <p className="font-hand text-3xl text-[#D95F59]">psst… it's locked, my love</p>
        <h1 className="font-display text-3xl tracking-tight mt-1 mb-6">
          Solve today's riddle to enter
        </h1>

        {riddle ? (
          <>
            <div className="rounded-2xl bg-[#FFF5F5] border border-[#EADEDA] p-6 mb-5">
              <p className="font-display text-xl md:text-2xl italic leading-snug" data-testid="riddle-question">
                {riddle.question}
              </p>
            </div>

            <form onSubmit={submit} className="space-y-3">
              <input
                data-testid="gate-answer-input"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder="Type your answer…"
                autoFocus
                className={`w-full rounded-full border px-5 py-3 bg-white outline-none text-center text-lg transition-colors ${
                  error ? "border-[#D95F59]" : "border-[#EADEDA] focus:border-[#D95F59]"
                }`}
              />
              {error && (
                <p data-testid="gate-error" className="font-hand text-2xl text-[#D95F59]">
                  not quite, try again 💗
                </p>
              )}
              <button
                type="submit"
                data-testid="gate-submit-btn"
                disabled={checking}
                className="w-full flex items-center justify-center gap-2 rounded-full bg-[#D95F59] text-white py-3 text-lg hover:bg-[#C75D53] hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-60"
              >
                {checking ? <Loader2 className="w-5 h-5 animate-spin" /> : <KeyRound className="w-5 h-5" />}
                Unlock our world
              </button>
            </form>

            <button
              data-testid="gate-hint-btn"
              onClick={() => setShowHint((s) => !s)}
              className="mt-4 text-sm text-[#756D6A] hover:text-[#2A2726]"
            >
              {showHint ? `Hint: ${riddle.hint}` : "Need a hint?"}
            </button>
          </>
        ) : (
          <Heart className="w-8 h-8 text-[#D95F59] fill-[#D95F59] animate-pulse mx-auto" />
        )}

        <p className="mt-8 font-hand text-xl text-[#756D6A]">
          for {names[1]}, from {names[0]} 💕
        </p>
      </motion.div>
    </div>
  );
}
